import { useEffect, useRef } from 'react';
import { browserEnvironment } from '../../lib/supabase';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
    };
  }
}

export function Turnstile({ onToken }: { onToken: (token: string | undefined) => void }) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sitekey = browserEnvironment.VITE_TURNSTILE_SITE_KEY;
    if (!sitekey || !container.current) return;
    let widgetId: string | undefined;
    const render = () => {
      if (window.turnstile && container.current && !widgetId)
        widgetId = window.turnstile.render(container.current, {
          sitekey,
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(undefined),
          'error-callback': () => onToken(undefined)
        });
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-evw-turnstile]');
    if (existing) {
      existing.addEventListener('load', render);
      render();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset['evwTurnstile'] = 'true';
      script.addEventListener('load', render);
      document.head.append(script);
    }
    return () => {
      if (widgetId) window.turnstile?.remove(widgetId);
      existing?.removeEventListener('load', render);
    };
  }, [onToken]);
  if (!browserEnvironment.VITE_TURNSTILE_SITE_KEY)
    return <p className="muted">Bot verification is disabled only for local development.</p>;
  return <div ref={container} aria-label="Bot verification" />;
}
